import { Injectable } from '@angular/core';
import { PeopleLookupApiService, MLSExternalApiService } from '@tangilla/tg-ng-core';
import { RequestParameters } from '@tangilla/tg-ng-models';
import { SurveyService } from 'src/app/core/services/survey/survey.service';
import { ErrorsService } from 'src/app/core/services/utils/errors.service';
import { ItemValue } from 'survey-core';
import { switchMap, map, catchError } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';

/**
 * Service responsible for handling MLS Provider Transfer form logic and API integrations.
 * 
 * This service manages the complete flow of MLS provider transfers including:
 * - License validation and person lookup
 * - Agent selection and data fetching
 * - Office selection with hierarchical organization
 * - Firm broker determination and conditional logic
 * - Dynamic form behavior based on user selections
 * 
 * @injectable
 * @since 1.0.0/Users/pabloromero/Documents/Repos/tangilla/assets/common/form_definitions_v2/mls_provider_transfer_form.json
 */
@Injectable({
  providedIn: 'root'
})
export class MlsProviderTransferService {

    /**
     * Creates an instance of MlsProviderTransferService.
     * 
     * @param surveyService - Service for managing SurveyJS form interactions
     * @param peopleLookupApiService - Service for person and relationship data lookup
     * @param mlsExternalApiService - Service for MLS-related API calls
     * @param errorsService - Service for error handling and user notifications
     */
    constructor(
      private surveyService: SurveyService,
      private peopleLookupApiService: PeopleLookupApiService,
      private mlsExternalApiService: MLSExternalApiService,
      private errorsService: ErrorsService
    ) {
    }


  /**
   * Initializes the MLS Provider Transfer form by setting up page rendering and value change handlers.
   * 
   * This method should be called when the form is first loaded to establish all necessary
   * event listeners and reactive behaviors.
   * 
   * @public
   * @returns {void}
   */
  initializeForm() {
    this.initializeOnAfterRenderPage();
    this.initializeOnValueChanged();
  }

  /**
   * Sets up page-specific initialization logic that runs after each page is rendered.
   * 
   * Currently handles:
   * - Office Details page: Triggers office agent fetching
   * 
   * @private
   * @returns {void}
   */
  initializeOnAfterRenderPage() {
    this.surveyService.surveyModel.onAfterRenderPage.add((sender, options) => {
      const currentPage = sender.currentPage;
      switch(currentPage.name) {
        case 'Office Details':
          this.fetchOfficeAgents();
          break;
      }
    })
  }

  /**
   * Sets up value change handlers for form fields that trigger specific business logic.
   * 
   * Handles the following field changes:
   * - LicType: Triggers license validation and agent lookup
   * - mls_login: Triggers agent data fetching and office selection setup
   * - office_mls_id: Manages conditional display of moving_entire_corporation question
   * 
   * @private
   * @returns {void}
   */
  initializeOnValueChanged() {
    this.surveyService.surveyModel.onValueChanged.add( (sender, options) => {
      if(options.name === 'LicType') {
        // Check if license type is Broker or Appraiser, otherwise stop the form
        if (options.value !== 'Broker' && options.value !== 'Appraiser') {
          this.errorsService.exitForm('Invalid License Type', 'MLS Provider Transfer is only available for Broker and Appraiser license types.');
          return;
        }

        const LicNumber = this.surveyService.surveyModel.getValue('LicNumber');
        const requestParameters = new RequestParameters(null);
        requestParameters.additionalFields = {
          include_relationships: true,
          license_number: LicNumber
        }
        
        // Fetch person record by license number
        this.peopleLookupApiService.get(requestParameters)
          .subscribe({
            next: (response: any) => {
              const person = response[0]
              if(person) {
                this.surveyService.surveyModel.setValue('person_id', person.person_id)
                this.surveyService.surveyModel.setValue('relationship_id', person.primary_relationship.relationship_id)
                this.surveyService.surveyModel.setValue('idp_user_name', person.primary_relationship.idp_user_name);
              } else {
                this.surveyService.surveyModel.setValue('person_id', null);
                this.surveyService.surveyModel.setValue('relationship_id', null);
                this.surveyService.surveyModel.setValue('idp_user_name', null);
              }
            },
            error: (error: any) => {
              this.errorsService.processError(error,'soft');
            }
          });

        // Fetch MLS agents by license number
        this.surveyService.isLoading = true;
        this.mlsExternalApiService.getAgentsByLicenseNumber(LicNumber)
          .subscribe({
            next: (agents: any[]) => {
              this.surveyService.isLoading = true;
              const mlsLoginQuestion = this.surveyService.surveyModel.getQuestionByName('mls_login');
              if (mlsLoginQuestion) {
                // Create ItemValue objects for each agent using MemberMlsId
                const agentChoices = agents.map(agent => 
                  new ItemValue(agent.MemberMlsId, `Username: ${agent.MemberMlsId} - Status: ${agent.MemberStatus} - Office: ${agent.OfficeMlsId}`)
                );
                
                // Set the choices for the mls_login question
                mlsLoginQuestion['choices'] = agentChoices;
                
                if (agents.length > 1) {
                  // Make the question visible if there are multiple agents
                  mlsLoginQuestion.visible = true;
                } else if (agents.length === 1) {
                  // If only one agent, set it as the value and keep question hidden
                  mlsLoginQuestion.visible = false;
                  mlsLoginQuestion.value = agents[0].MemberMlsId;
                  // Fetch agent data for the single agent
                  this.fetchAgentData(agents[0].MemberMlsId);
                } else {
                  // Stop form
                  this.errorsService.exitForm('No MLS User', 'No MLS agents found for the provided license number.');
                }
                this.surveyService.isLoading = false
              }
            },
            error: (error: any) => {
              this.errorsService.processError(error, 'soft');
              // Hide mls_login question on error
              const mlsLoginQuestion = this.surveyService.surveyModel.getQuestionByName('mls_login');
              if (mlsLoginQuestion) {
                mlsLoginQuestion.visible = false;
                mlsLoginQuestion['choices'] = [];
              }
            }
          });
      }

      if(options.name === 'mls_login') {
        // Fetch agent data when mls_login value changes
        if (options.value) {
          this.fetchAgentData(options.value);
        }
      }

      if(options.name === 'office_mls_id') {
        // Check if selected office is a main office and show moving_entire_corporation question
        if (options.value) {
          const isFirmBroker = this.surveyService.surveyModel.getVariable('is_firm_broker');
          
          // Only run this logic if the agent is a firm broker
          if (isFirmBroker) {
            const mlsOfficesArray = this.surveyService.surveyModel.getVariable('mls_offices_array');
            const movingEntireCorporationQuestion = this.surveyService.surveyModel.getQuestionByName('moving_entire_corporation');
            
            if (mlsOfficesArray && movingEntireCorporationQuestion) {
              // Check if the selected office is a main office (has other offices pointing to it as MainOfficeMlsId)
              const isMainOffice = mlsOfficesArray.some((office: any) => 
                office.MainOfficeMlsId === options.value
              );
              
              if (isMainOffice) {
                // Selected office is a main office, show the question
                movingEntireCorporationQuestion.visible = true;
                movingEntireCorporationQuestion.isRequired = true;
              } else {
                // Selected office is not a main office, hide the question
                movingEntireCorporationQuestion.visible = false;
                movingEntireCorporationQuestion.isRequired = false;
                movingEntireCorporationQuestion.value = null;
              }
            }
          }
        }
      }
    })
  }

  /**
   * Fetches and stores agent data and associated office information for a given MLS login.
   * 
   * This method performs the following operations:
   * 1. Retrieves agent details by MLS login
   * 2. Fetches all offices associated with the agent
   * 3. Organizes offices hierarchically (main/branch/single)
   * 4. Determines if the agent is a firm broker
   * 5. Updates form questions with organized office choices
   * 
   * @private
   * @param {string} mlsLogin - The MLS login identifier for the agent
   * @returns {void}
   */
  private fetchAgentData(mlsLogin: string) {
    this.surveyService.isLoading = true;
    this.mlsExternalApiService.getAgentByMLSLogin(mlsLogin)
      .pipe(
        switchMap((agentData: any) => {
          // Store agent data in survey model
          this.surveyService.surveyModel.setVariable('ntreis_user', agentData);
          
          // Chain to fetch offices for this agent
          return this.mlsExternalApiService.getOfficesByMLSLogin(mlsLogin);
        }),
        // finalize(() => this.surveyService.isLoading = false)
      )
      .subscribe({
        next: (offices: any[]) => {
          this.surveyService.surveyModel.setVariable('mls_offices_array', offices);
          const officeMlsIdQuestion = this.surveyService.surveyModel.getQuestionByName('office_mls_id');
          if (officeMlsIdQuestion) {
            // Organize offices hierarchically and add appropriate labels
            const organizedChoices = this.organizeOfficeChoices(offices);
            
            // Set the choices for the office_mls_id question
            officeMlsIdQuestion['choices'] = organizedChoices;
            officeMlsIdQuestion.visible = true;
          }

          // Determine if agent is a firm broker
          // TODO: if selecting single office, we shouldn't show the question
          const isFirmBroker = this.determineFirmBroker(offices);
          this.surveyService.surveyModel.setVariable('is_firm_broker', isFirmBroker);
          this.surveyService.isLoading = false
        },
        error: (error: any) => {
          this.surveyService.isLoading = false
          this.errorsService.processError(error, 'soft');
        }
      });
  }

  /**
   * Fetches agents for the currently selected office and generates formatted HTML display.
   * 
   * This method:
   * 1. Retrieves all agents for the selected office
   * 2. Stores the agent data in survey variables
   * 3. Identifies any branch offices if the selected office is a main office
   * 4. Generates styled HTML content showing office and agent information
   * 5. Conditionally displays branch office details based on moving_entire_corporation selection
   * 
   * @private
   * @returns {void}
   */
  private fetchOfficeAgents() {
    const selectedOfficeMlsId = this.surveyService.surveyModel.getValue('office_mls_id');
    if (selectedOfficeMlsId) {
      this.surveyService.isLoading = true;
      this.mlsExternalApiService.getAgentsByOfficeMlsId(selectedOfficeMlsId)
        // .pipe(
        //   finalize(() => this.surveyService.isLoading = false)
        // )
        .subscribe({
          next: (agents: any[]) => {
            // Save agents array in a SurveyJS variable
            this.surveyService.surveyModel.setVariable('selected_ntreis_office_agents_array', agents);
            
            // Get office information from mls_offices_array
            const mlsOfficesArray = this.surveyService.surveyModel.getVariable('mls_offices_array');
            const selectedOffice = mlsOfficesArray?.find((office: any) => office.OfficeMlsId === selectedOfficeMlsId);
            
            if (selectedOffice) {
              const agentCount = agents.length;
              const agentText = agentCount === 1 ? 'agent' : 'agents';
              
              // Check if this office is a main office (has branch offices)
              const branchOffices = mlsOfficesArray?.filter((office: any) => 
                office.MainOfficeMlsId === selectedOfficeMlsId &&
                office.MainOfficeMlsId !== office.OfficeMlsId // Avoid self-reference
              ) || [];
              
              this.generateOfficeHtml(selectedOffice, agentCount, agentText, branchOffices);
              // this.surveyService.isLoading = false
            }
          },
          error: (error: any) => {
            this.surveyService.isLoading = false
            this.errorsService.processError(error, 'soft');
          }
        });
    }
  }

  /**
   * Generates formatted HTML content displaying office information and optionally branch offices.
   * 
   * Creates a styled card layout showing:
   * - Main office name, MLS ID, and agent count
   * - Branch office details (only if moving_entire_corporation is true)
   * - Agent counts for each branch office (fetched asynchronously)
   * 
   * @private
   * @param {any} selectedOffice - The main office object containing office details
   * @param {number} agentCount - Number of agents in the main office
   * @param {string} agentText - Singular or plural form of "agent" based on count
   * @param {any[]} branchOffices - Array of branch office objects
   * @returns {void}
   */
  private generateOfficeHtml(selectedOffice: any, agentCount: number, agentText: string, branchOffices: any[]) {
    let officeHtml = `
      <div style="padding: 12px; border: 1px solid #ddd; border-radius: 8px; background-color: #f8f9fa;">
        <h4 style="margin: 0 0 8px 0; color: #2c3e50; font-size: 16px; font-weight: 600;">
          ${selectedOffice.OfficeName}
        </h4>
        <p style="margin: 0; color: #6c757d; font-size: 14px;">
          <strong>MLS ID:</strong> ${selectedOffice.OfficeMlsId}
        </p>
        <p style="margin: 4px 0 0 0; color: #28a745; font-size: 14px; font-weight: 500;">
          <span style="background-color: #d4edda; padding: 2px 6px; border-radius: 4px;">
            ${agentCount} ${agentText}
          </span>
        </p>
    `;

    // If this is a main office with branch offices, fetch and display branch office information
    // Only show branches if moving_entire_corporation is true
    const movingEntireCorporation = this.surveyService.surveyModel.getValue('moving_entire_corporation');
    if (branchOffices.length > 0 && movingEntireCorporation === true) {
      officeHtml += `
        <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #dee2e6;">
          <h5 style="margin: 0 0 8px 0; color: #495057; font-size: 14px; font-weight: 600;">
            Branch Offices (${branchOffices.length}):
          </h5>
      `;

      // Create observables for each branch office agent count
      const branchOfficeObservables = branchOffices.map(branchOffice => 
        this.mlsExternalApiService.getAgentsByOfficeMlsId(branchOffice.OfficeMlsId)
          .pipe(
            map(branchAgents => ({
              office: branchOffice,
              agentCount: branchAgents?.length || 0
            })),
            catchError(() => of({
              office: branchOffice,
              agentCount: 0
            }))
          )
      );

      // Set loading state for branch office data
      this.surveyService.isLoading = true;
      
      // Use forkJoin to execute all observables in parallel
      forkJoin(branchOfficeObservables)
        // .pipe(
        //   finalize(() => this.surveyService.isLoading = false)
        // )
        .subscribe({
          next: (branchOfficeData) => {
            branchOfficeData.forEach(({ office, agentCount: branchAgentCount }) => {
              const branchAgentText = branchAgentCount === 1 ? 'agent' : 'agents';
              officeHtml += `
                <div style="margin: 8px 0; padding: 8px; background-color: #ffffff; border: 1px solid #e9ecef; border-radius: 4px;">
                  <p style="margin: 0; font-size: 13px; color: #495057;">
                    <strong>${office.OfficeName}</strong> - MLS ID: ${office.OfficeMlsId}
                  </p>
                  <p style="margin: 2px 0 0 0; font-size: 12px; color: #6c757d;">
                    <span style="background-color: #e7f3ff; padding: 1px 4px; border-radius: 3px;">
                      ${branchAgentCount} ${branchAgentText}
                    </span>
                  </p>
                </div>
              `;
            });
            
            officeHtml += `</div></div>`;
            
            // Set the final HTML content for the question
            this.surveyService.surveyModel.getQuestionByName('selected_ntreis_office_agents')['html'] = officeHtml;
            this.surveyService.isLoading = false;
          },
          error: (error: any) => {
            this.errorsService.processError(error, 'soft');
            // Still show the main office even if branch office data fails
            officeHtml += `</div></div>`;
            this.surveyService.surveyModel.getQuestionByName('selected_ntreis_office_agents')['html'] = officeHtml;
            this.surveyService.isLoading = false;
          }
        });
    } else {
      officeHtml += `</div>`;
      
      // Set the HTML content for the question (no branch offices)
      this.surveyService.surveyModel.getQuestionByName('selected_ntreis_office_agents')['html'] = officeHtml;
      this.surveyService.isLoading = false;
    }
  }

  /**
   * Organizes office data into a hierarchical structure for dropdown display.
   * 
   * This method categorizes offices into three types:
   * - Main/Firm offices: Offices that have other offices as branches
   * - Branch offices: Offices that belong to a main office
   * - Single offices: Independent offices with no hierarchy
   * 
   * The resulting ItemValue array displays:
   * - Main offices first, labeled as "(Firm)"
   * - Branch offices indented under their main office, labeled as "(Branch)"
   * - Single offices last, labeled as "(Single)"
   * 
   * @private
   * @param {any[]} offices - Array of office objects from the MLS API
   * @returns {ItemValue[]} Organized array of ItemValue objects for SurveyJS dropdown
   */
  private organizeOfficeChoices(offices: any[]): ItemValue[] {
    const officeChoices: ItemValue[] = [];
    const officeIds = offices.map(office => office.OfficeMlsId);
    
    // Separate offices into main, branch, and single offices
    const mainOffices = offices.filter(office => 
      offices.some(otherOffice => otherOffice.MainOfficeMlsId === office.OfficeMlsId)
    );
    
    const branchOffices = offices.filter(office => 
      office.MainOfficeMlsId && 
      officeIds.includes(office.MainOfficeMlsId) &&
      office.MainOfficeMlsId !== office.OfficeMlsId // Avoid self-reference
    );
    
    const singleOffices = offices.filter(office => 
      !office.MainOfficeMlsId && !offices.some(otherOffice => otherOffice.MainOfficeMlsId === office.OfficeMlsId)
    );

    // Add main offices first
    mainOffices.forEach(office => {
      const officeLabel = `${office.OfficeName} (${office.OfficeMlsId}) - Status: ${office.OfficeStatus} (Firm)`;
      officeChoices.push(new ItemValue(office.OfficeMlsId, officeLabel));
      
      // Add branch offices under their main office
      const relatedBranches = branchOffices.filter(branch => 
        branch.MainOfficeMlsId === office.OfficeMlsId
      );
      relatedBranches.forEach(branch => {
        const branchLabel = `    ${branch.OfficeName} (${branch.OfficeMlsId}) - Status: ${branch.OfficeStatus} (Branch)`;
        officeChoices.push(new ItemValue(branch.OfficeMlsId, branchLabel));
      });
    });

    // Add single offices
    singleOffices.forEach(office => {
      const officeLabel = `${office.OfficeName} (${office.OfficeMlsId}) - Status: ${office.OfficeStatus} (Single)`;
      officeChoices.push(new ItemValue(office.OfficeMlsId, officeLabel));
    });

    return officeChoices;
  }

  /**
   * Determines if an agent is a firm broker based on their office relationships.
   * 
   * An agent is considered a firm broker if they have access to offices where:
   * - At least one office has a MainOfficeMlsId that points to another office in their list
   * - This indicates the agent has access to both main and branch offices
   * 
   * @private
   * @param {any[]} offices - Array of office objects associated with the agent
   * @returns {boolean} True if the agent is a firm broker, false otherwise
   */
  private determineFirmBroker(offices: any[]): boolean {
    // Get all OfficeMlsId values from the offices list
    const officeIds = offices.map(office => office.OfficeMlsId);
    
    // Check if any office has a MainOfficeMlsId that matches another office's OfficeMlsId
    return offices.some(office => 
      office.MainOfficeMlsId && officeIds.includes(office.MainOfficeMlsId)
    );
  }

}